import { forwardRef } from "react";
import PropTypes from "prop-types";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { displayName, isAgent, relativeTime, userLabel } from "./userDisplay";
import UserAvatar from "../people/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Paperclip, UserRound } from "lucide-react";

/**
 * Maps a 0-1000 priority value to a visual band.
 *
 * The spec left this bucketing open; we pick four roughly-equal bands so
 * high-priority work is easily spotted without the color becoming noise.
 * Tune during manual QA if it looks wrong.
 */
const priorityBand = (priority) => {
  if (priority == null) return "normal";
  if (priority < 250) return "high";
  if (priority < 500) return "medium";
  if (priority < 750) return "normal";
  return "low";
};

// The band is the card's left edge, in tokens: brick for high (the accent
// the theme reserves for emphasis), amber for medium, moss for normal, and
// a faint grey for low.
const BAND_BORDER_CLASS = {
  high: "border-l-destructive",
  medium: "border-l-warning",
  // Moss, not grey: the grey read as a white edge in dark mode (#36).
  normal: "border-l-primary",
  low: "border-l-muted-foreground/40",
};

/**
 * Presentational card — the single source of the card's markup. Used by
 * the sortable TaskCard below and, standalone, by TaskBoard's DragOverlay
 * (the floating copy that follows the pointer must look identical to the
 * card it lifted from). Dense on purpose (#33): title, then one row of
 * chips.
 */
export const TaskCardView = forwardRef(function TaskCardView(
  { task, project = null, style = {}, className = "", ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        "task-card rounded-md border border-l-4 bg-card px-3 py-2 dark:bg-secondary text-card-foreground shadow-xs",
        BAND_BORDER_CLASS[priorityBand(task.priority)],
        className,
      )}
      style={style}
      {...rest}
    >
      <div className="font-semibold">{task.title}</div>
      {/* Always a chip row: every card says who's on it — the assignee's
          avatar, or the unassigned placeholder (#37). */}
      <div className="mt-1 flex flex-wrap items-center gap-1">
        {project && (
          <Badge variant="secondary" className="dark:bg-background">
            {project.name}
          </Badge>
        )}
        {/* A real space before each chip (inside its conditional): the
              flex gap is visual only, and without it the row reads as
              "Housebrandon is on it3" to screen readers and agents. */}
        {task.assignee && (
          <>
            {" "}
            <span
              className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-secondary-foreground dark:bg-background"
              role="img"
              title={`Assigned to ${userLabel(task.assignee)}`}
              aria-label={`Assigned to ${userLabel(task.assignee)}`}
            >
              <UserAvatar
                user={task.assignee}
                size="xs"
                decorative
                className="-ml-1.5 dark:ring-1 dark:ring-border"
              />
              {isAgent(task.assignee) && (
                <span className="font-normal" aria-hidden="true">
                  agent
                </span>
              )}
            </span>
          </>
        )}
        {/* Unassigned: a neutral dashed circle where the avatar would be
              (board #37), so a glance tells "nobody" from "missing". It has
              no text, so it needs no separating space. */}
        {!task.assignee && (
          <>
            <span
              className="inline-flex size-5 items-center justify-center rounded-full border border-dashed border-muted-foreground/60 text-muted-foreground"
              role="img"
              title="Unassigned"
              aria-label="Unassigned"
            >
              <UserRound className="size-3" aria-hidden="true" />
            </span>
          </>
        )}
        {task.claimed_by && (
          <>
            {" "}
            <Badge
              variant="warning"
              title={`Claimed by ${displayName(task.claimed_by)} ${relativeTime(
                task.claimed_at,
              )}`}
            >
              <UserAvatar
                user={task.claimed_by}
                size="xs"
                decorative
                className="-ml-1 mr-1 size-4 text-[8px]"
              />
              {displayName(task.claimed_by)} is on it
            </Badge>
          </>
        )}
        {task.attachment_count > 0 && (
          <>
            {" "}
            <span
              className="inline-flex items-center gap-0.5 text-xs text-muted-foreground"
              role="img"
              aria-label={`${task.attachment_count} ${
                task.attachment_count === 1 ? "attachment" : "attachments"
              }`}
              title={`${task.attachment_count} ${
                task.attachment_count === 1 ? "attachment" : "attachments"
              }`}
            >
              <Paperclip className="size-3.5" aria-hidden="true" />
              <span aria-hidden="true">{task.attachment_count}</span>
            </span>
          </>
        )}
      </div>
    </div>
  );
});

const userShape = PropTypes.shape({
  id: PropTypes.number,
  username: PropTypes.string,
  kind: PropTypes.string,
});

const taskShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  priority: PropTypes.number,
  project: PropTypes.number,
  assignee: userShape,
  claimed_by: userShape,
  claimed_at: PropTypes.string,
  attachment_count: PropTypes.number,
});

TaskCardView.propTypes = {
  task: taskShape.isRequired,
  project: PropTypes.object,
  style: PropTypes.object,
  className: PropTypes.string,
};

function TaskCard({
  task,
  showProjectBadge = false,
  projectsById = {},
  onClick = undefined,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const project =
    showProjectBadge && task.project != null
      ? projectsById[task.project]
      : null;

  const clickable = Boolean(onClick);

  const handleClick = () => {
    if (onClick) onClick(task);
  };

  // Keyboard: dnd-kit's sensor owns Space (pick up / drop), the arrows
  // (move) and Escape (cancel) — TaskBoard configures those keys so Enter
  // is NOT a drag start. Enter opens the editor. Both handlers must run:
  // spreading `{...listeners}` and then setting onKeyDown REPLACED the
  // sensor's handler and keyboard dragging silently never worked (#33
  // review, pre-existing on main).
  const handleKeyDown = (event) => {
    listeners?.onKeyDown?.(event);
    if (!onClick || isDragging) return;
    if (event.key === "Enter") {
      event.preventDefault();
      onClick(task);
    }
  };

  // While dragging, the DragOverlay carries the full-strength card; this
  // in-list node stays behind as a dimmed placeholder. It keeps the
  // sorting TRANSFORM (without it, dnd-kit shifts the neighbor into the
  // active card's slot and the two render stacked on every same-column
  // drag) but drops the TRANSITION: when onDragOver re-parents this node
  // into another column, the transform is briefly computed from the
  // pre-move rect, and animating from that stale value flings the card
  // across the screen. Unanimated, the wrong frame self-corrects on the
  // next measure and is invisible.
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0.4 : 1,
    cursor: clickable ? "pointer" : undefined,
  };

  return (
    <TaskCardView
      ref={setNodeRef}
      task={task}
      project={project}
      className="mb-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      style={style}
      {...attributes}
      {...listeners}
      role={clickable ? "button" : undefined}
      aria-label={clickable ? `Edit task ${task.title}` : undefined}
      onClick={clickable ? handleClick : undefined}
      onKeyDown={clickable ? handleKeyDown : undefined}
    />
  );
}

TaskCard.propTypes = {
  task: taskShape.isRequired,
  showProjectBadge: PropTypes.bool,
  projectsById: PropTypes.object,
  onClick: PropTypes.func,
};

export default TaskCard;
